import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useTenant } from "../context/TenantContext";
import "../styles/UpdatePrompt.css";
import logger from '../utils/logger';

const UpdateApiKey = () => {
  const { tenant } = useTenant();
  const tenantId = tenant?.id || window.__TENANT_ID__ || localStorage.getItem("superadmin_tenant_id");

  const [apiKey, setApiKey] = useState("");
  const [maskedKey, setMaskedKey] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [fetching, setFetching] = useState(true);
  const [showKey, setShowKey] = useState(false);


  useEffect(() => {
    if (tenantId) fetchConfig();
  }, [tenantId]);

  const fetchConfig = async () => {
    try {
      const res = await api.get(`/api/tenants/${tenantId}/ai-config`);
      if (res.data.success) {
        setHasKey(res.data.hasKey);
        setMaskedKey(res.data.maskedKey);
        setUpdatedAt(res.data.updatedAt);
      }
    } catch (error) {
      logger.error("Error fetching config:", error);
      setMessage("Could not load current configuration");
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setMessage("Please enter an API key");
      return;
    }

    if (!apiKey.trim().startsWith("sk-")) {
      setMessage("Invalid API key format. OpenAI keys start with 'sk-'");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.put(`/api/tenants/${tenantId}/ai-config`, {
        openaiApiKey: apiKey.trim(),
      });

      if (res.data.success) {
        setMessage("OpenAI API key saved successfully!");
        setMaskedKey(res.data.maskedKey);
        setHasKey(true);
        setUpdatedAt(new Date().toISOString());
        setApiKey("");
        setShowKey(false);
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message;
      setMessage(`Error: ${errMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Are you sure you want to remove the OpenAI API key? AI features will stop working for this account.")) {
      return;
    }

    setDeleting(true);
    setMessage("");

    try {
      const res = await api.delete(`/api/tenants/${tenantId}/ai-config`);
      if (res.data.success) {
        setMessage("API key removed successfully");
        setHasKey(false);
        setMaskedKey(null);
        setUpdatedAt(null);
        setApiKey("");
      }
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  if (fetching) {
    return (
      <div className="update-container">
        <h2>Loading configuration...</h2>
      </div>
    );
  }

  return (
    <div className="update-container">
      <h2>OpenAI API Key Configuration</h2>

      {/* Current Status */}
      <div style={{
        padding: "16px 20px",
        borderRadius: "8px",
        marginBottom: "24px",
        backgroundColor: hasKey ? "#f0fdf4" : "#fef2f2",
        border: `1px solid ${hasKey ? "#86efac" : "#fca5a5"}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontSize: "20px" }}>{hasKey ? "\u2705" : "\u274C"}</span>
          <strong style={{ color: hasKey ? "#166534" : "#991b1b" }}>
            {hasKey ? "API Key Configured" : "No API Key Configured"}
          </strong>
        </div>
        {hasKey && maskedKey && (
          <div style={{ fontSize: "14px", color: "#374151" }}>
            <div>Current Key: <code style={{ backgroundColor: "#f3f4f6", padding: "2px 8px", borderRadius: "4px" }}>{maskedKey}</code></div>
            {updatedAt && (
              <div style={{ marginTop: "4px", color: "#6b7280", fontSize: "13px" }}>
                Last updated: {new Date(updatedAt).toLocaleString()}
              </div>
            )}
          </div>
        )}
        {!hasKey && (
          <p style={{ fontSize: "13px", color: "#991b1b", margin: 0 }}>
            AI features (medication extraction, text analysis) will not work until an API key is configured.
          </p>
        )}
      </div>

      {/* Update Key Section */}
      <div className="prompt-section">
        <label>{hasKey ? "Update OpenAI API Key" : "Add OpenAI API Key"}</label>
        <div style={{ position: "relative" }}>
          <input
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="sk-proj-..."
            style={{
              width: "100%",
              padding: "12px 50px 12px 14px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontSize: "15px",
              fontFamily: "monospace",
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              color: "#6b7280",
              padding: "4px",
              marginTop : "0px"
            }}
          >
            {showKey ? "Hide" : "Show"}
          </button>
        </div>

        <p style={{ fontSize: "13px", color: "#6b7280", margin: "8px 0 0 0" }}>
          Get your API key from{" "}
          <a
            href="https://platform.openai.com/api-keys"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#2563eb" }}
          >
            platform.openai.com/api-keys
          </a>
        </p>

        <div style={{ display: "flex", gap: "12px", marginTop: "14px" }}>
          <button
            onClick={handleSave}
            disabled={loading || !apiKey.trim()}
            className={loading ? "loading" : ""}
            style={{ flex: 1 }}
          >
            {loading ? "Saving..." : hasKey ? "Update Key" : "Save Key"}
          </button>

          {hasKey && (
            <button
              onClick={handleRemove}
              disabled={deleting}
              style={{
                padding: "10px 20px",
                background: deleting ? "#ccc" : "#dc2626",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                cursor: deleting ? "not-allowed" : "pointer",
              }}
            >
              {deleting ? "Removing..." : "Remove Key"}
            </button>
          )}
        </div>

        {message && (
          <p
            className={`status-message ${message.includes("successfully") || message.includes("saved") ? "success" : "error"}`}
            style={{
              marginTop: "14px",
              padding: "10px",
              borderRadius: "6px",
              backgroundColor: message.includes("successfully") || message.includes("saved") ? "#f0fdf4" : "#fef2f2",
              color: message.includes("successfully") || message.includes("saved") ? "#166534" : "#991b1b",
            }}
          >
            {message}
          </p>
        )}
      </div>

      {/* Info Section */}
      <div style={{
        padding: "16px 20px", borderRadius: "8px", marginTop: "24px",
        backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
        fontSize: "13px", color: "#1e40af", lineHeight: "1.6",
      }}>
        <strong>How it works:</strong>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li>Each sub-account uses its own OpenAI API key for AI features</li>
          <li>The key is encrypted before being stored in the database</li>
          <li>Used for medication extraction (Camera, Upload) in Client and Follow-up</li>
          <li>Billing goes directly to the OpenAI account associated with this key</li>
        </ul>
      </div>
    </div>
  );
};

export default UpdateApiKey;
